import { useCallback } from "react";
import { useSelector } from "react-redux";
import type { FieldConfiguration } from "../../../../../../../../api/types/fieldConfigurationTypes.ts";
import type { ConditionField, DimensionDescriptor, ReportField } from "../../../../../../../../api/types/svTypes.ts";
import { useAppDispatch } from "../../../../store/hooks/useAppDispatch.ts";
import { selectAutoShowItemOptionsId } from "../../../../store/selectors/currentReportSelectors.ts";
import { selectBuilderDetailedView } from "../../../../store/selectors/fieldsStateSelectors.ts";
import { createCurrentReportAction } from "../../../../store/utils/storeUtils.ts";
import CustomConditions from "../../../viewer/common/values/CustomConditions.tsx";

interface Props {
  field: ReportField;
  dimensions: DimensionDescriptor[];
  globalConditions: ConditionField[];
  canToggleParameter?: boolean;
  readonly: boolean;
  popupAnchorEl: HTMLElement | undefined;
  onClosePopup: () => void;
  onUpdateField: (changes: Partial<FieldConfiguration>) => void;
}

const FieldConditionsPanel = ({
  field,
  dimensions,
  globalConditions,
  canToggleParameter,
  readonly,
  popupAnchorEl,
  onClosePopup,
  onUpdateField,
}: Props) => {
  const dispatch = useAppDispatch();
  const autoShowItemOptionsId = useSelector(selectAutoShowItemOptionsId);
  const detailedView = useSelector(selectBuilderDetailedView);

  const handleUpdateField = useCallback(
    (_: unknown, changes: Partial<FieldConfiguration>) => onUpdateField({ ...changes, useCustomConditions: true }),
    [onUpdateField]
  );

  const handleSetAutoShowItemOptionsId = useCallback(
    (id?: string) => dispatch(createCurrentReportAction("setAutoShowItemOptionsId", id)),
    [dispatch]
  );

  return (
    <CustomConditions
      field={{ ...field, config: { ...field.config, useCustomConditions: true } }}
      dimensions={dimensions}
      globalConditions={globalConditions}
      canToggleParameter={canToggleParameter}
      canBeRemoved
      isReadonlyCustomLabel
      updateField={handleUpdateField}
      autoShowItemOptionsId={autoShowItemOptionsId}
      onSetAutoShowItemOptionsId={handleSetAutoShowItemOptionsId}
      readonly={readonly}
      detailedView={detailedView}
      popupAnchorEl={popupAnchorEl}
      onClosePopup={onClosePopup}
    />
  );
};

export default FieldConditionsPanel;
